import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { __ } from '@wordpress/i18n';
import { smashBlocks } from "../blocks";
import FeedCTA from '../cta';

const elementorWidgets = smashBlocks.map((block) => ({
    blockId: block.id,
    name: `smash-${block.id}-feed`,
    title: block.title,
    icon: `eicon-${block.id === 'reviews' ? 'review' : block.id}`,
    categories: ['smashballoon'],
    customizerPage: block.customizerPage,
}));

const getElementorWidgetConfig = (blockId) => {
    return elementorWidgets.find((w) => w.blockId === blockId) || null;
};

const enablePreviewLinks = (container) => {
    if (!container || container.dataset.sbcLinksEnabled) return;
    container.dataset.sbcLinksEnabled = 'true';

    container.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]');
        const href = link?.getAttribute('href');
        if (href && href !== '#' && !href.startsWith('javascript:')) {
            e.preventDefault();
            e.stopPropagation();
            window.open(link.href, '_blank', 'noopener');
        }
    });
};

const elementorPreviewInit = (widgetName, initCallback) => {
    if (
        typeof window !== 'undefined' &&
        window.elementorFrontend &&
        window.elementorFrontend.hooks
    ) {
        window.elementorFrontend.hooks.addAction(
            `frontend/element_ready/${widgetName}.default`,
            ($scope) => {
                const container = $scope?.[0] || $scope;
                if (typeof initCallback === 'function') {
                    initCallback(container);
                }
            }
        );
    }
};

const reorderPlaceholderOption = (panel) => {
    const $select = panel.$el.find('.elementor-control-feed_id select');
    if (!$select.length) return;

    const $placeholder = $select.find('option[value=""]');
    if (!$placeholder.length) return;
    if ($select.children().first().attr('value') === '') return;

    const currentVal = $select.val();
    $select.prepend($placeholder);
    if (currentVal) $select.val(currentVal);
};

const injectFeedLinks = (panel, feedUrl) => {
    const $control = panel.$el.find('.elementor-control-feed_id');
    if (!$control.length || !feedUrl) return;

    const parentJQuery = window.parent?.jQuery;
    if (!parentJQuery) return;

    const $wrapper = $control.find('.elementor-control-input-wrapper');

    // Remove existing links to avoid duplicates on panel reopen
    $wrapper.find('.sbc-elementor-feed-links').remove();

    const $select = $control.find('select');
    const selectedValue = $select.val();

    const editDisplay = selectedValue ? '' : 'display:none;';
    const separatorDisplay = selectedValue ? 'inline-block' : 'none';

    const $links = parentJQuery(
        '<div class="sbc-elementor-feed-links" style="font-weight:700; color:#a73061; margin-top:10px;">' +
        '<a class="sbc-edit-feed-link" href="' + feedUrl + '&feed_id=' + selectedValue + '" target="_blank" style="color:#a73061;' + editDisplay + '">' + __('Edit this Feed', 'sb-common') + '</a>' +
        '<span class="sbc-feed-links-separator" style="color:#aaa; display:' + separatorDisplay + '; margin:0 5px;">|</span>' +
        '<a class="sbc-create-feed-link" href="' + feedUrl + '" target="_blank" style="color:#a73061;">' + __('Create New Feed', 'sb-common') + '</a>' +
        '</div>'
    );

    $wrapper.append($links);

    // Update edit link visibility and URL on feed selection change
    $select.off('change.sbcFeedLinks').on('change.sbcFeedLinks', function () {
        const val = this.value;
        const $editLink = $wrapper.find('.sbc-edit-feed-link');
        const $separator = $wrapper.find('.sbc-feed-links-separator');

        if (val) {
            $editLink.attr('href', feedUrl + '&feed_id=' + val).show();
            $separator.css('display', 'inline-block');
        } else {
            $editLink.hide();
            $separator.hide();
        }
    });
};

const initElementorFeedWidget = ({
    blockId,
    widgetName,
    globalVar,
    feedInitFn,
    ctaRootId = 'sbc-elementor-cta-root',
}) => {
    const mountCTA = (container) => {
        const {
            feeds = [],
            feed_url: feedUrl = '',
            is_pro_active: isPro = false,
        } = window[globalVar] || {};

        const feedsOptions = [
            { label: __('Select a Feed', 'sb-common'), value: '' },
            ...feeds.map((feed) => ({
                label: feed.feed_name || `Feed ${feed.id}`,
                value: String(feed.id),
            })),
        ];

        const setAttributes = (attrs) => {
            if (attrs.feedId) {
                const parentJQuery = window.parent?.jQuery;
                if (parentJQuery) {
                    parentJQuery('#elementor-controls select[data-setting="feed_id"]')
                        .val(attrs.feedId)
                        .trigger('change');
                }
            }
        };

        const root = createRoot(container);
        root.render(
            createElement(FeedCTA, {
                id: blockId,
                feeds,
                feedsOptions,
                createFeedUrl: feedUrl,
                isPro,
                attributes: { feedId: '' },
                setAttributes,
            })
        );
    };

    const initWidget = () => {
        elementorPreviewInit(widgetName, (container) => {
            if (typeof feedInitFn === 'function') {
                feedInitFn();
            }
            const ctaRoot = container.querySelector?.(`#${ctaRootId}`);
            if (ctaRoot) {
                mountCTA(ctaRoot);
            }
            enablePreviewLinks(container);
        });
    };

    if (window.elementorFrontend?.hooks) {
        initWidget();
    } else {
        jQuery(window).on('elementor/frontend/init', initWidget);
    }

    const registerPanelHook = () => {
        if (!window.parent?.elementor?.hooks) return;
        window.parent.elementor.hooks.addAction(
            `panel/open_editor/widget/${widgetName}`,
            (panel) => {
                const { feed_url: feedUrl = '' } = window[globalVar] || {};

                reorderPlaceholderOption(panel);
                injectFeedLinks(panel, feedUrl);

                panel.$el
                    .find('.elementor-control-feed_id select')
                    .off('change.sbcFeedInit')
                    .on('change.sbcFeedInit', () => {
                        setTimeout(() => {
                            if (typeof feedInitFn === 'function') {
                                feedInitFn();
                            }
                        }, 4000);
                    });
            }
        );
    };

    if (window.parent?.elementor?.hooks) {
        registerPanelHook();
    } else {
        jQuery(window).on('elementor/frontend/init', registerPanelHook);
    }
};

export { elementorWidgets, getElementorWidgetConfig, elementorPreviewInit, initElementorFeedWidget };
